<ng-container *ngIf="overview">
  <ng-template #overviewActions>
    <button uiButton [size]="undefined" [wrap]="true" [text]="true" type="button" class="requirement-overview__manage" [disabled]="overviewNavigationBlocked" (click)="manageRequested.emit()">Manage requirements</button>
  </ng-template>
  <ui-panel panelTitle="Completion requirements" [headerActionsTemplate]="overviewActions" [noGrow]="true" [totalCount]="rows.length" data-work-requirement-overview>
    <div class="requirement-overview__body">
      <ul *ngIf="rows.length"><li *ngFor="let row of rows"><strong>{{ overviewLabel(row) }}</strong><p>{{ overviewDescription(row) }}</p></li></ul>
      <p *ngIf="!rows.length">No authored completion requirements.</p>
      <p>Counts describe authored settings, not satisfied evidence or execution readiness.</p>
    </div>
  </ui-panel>
</ng-container>
<phms-workflow-library-selector
  title="Add requirement" description="Choose what staff must provide before completing this step."
  searchLabel="Search requirement types" [categories]="libraryCategories" [entries]="libraryEntries"
  [open]="adding" [active]="active && !overview" [context]="libraryRenderContext()" [disabled]="disabled"
  [loading]="loading" [error]="catalogError" emptyMessage="No supported requirement types are installed for this workflow."
  (chooseRequested)="chooseLibraryProvider($event)" (ownerActionRequested)="requestLibraryOwner($event)"
  (opened)="libraryOpened()" (closed)="libraryClosed($event)" (retryRequested)="load()"
/>
<section class="typed-requirements" [hidden]="overview" aria-label="Completion requirements editor">
  <div class="requirements-toolbar">
    <p>Complete step checks every requirement for the current visit.</p>
    <button uiButton [size]="undefined" severity="primary" [outlined]="true" type="button" class="add-requirement"
      [disabled]="disabled || !completionAvailable || ambiguous || legacyCompletingActions || !availableProviders.length" (click)="startAdd()">
      <i class="pi pi-plus" aria-hidden="true"></i> Add requirement
    </button>
  </div>
  <p *ngIf="authoringIssue" role="status" class="configuration-warning">{{ authoringIssue }}</p>
  <p *ngIf="loading" role="status">Loading installed requirement types…</p>
  <p *ngIf="!loading && catalog && !completionAvailable" class="configuration-warning">An installed Complete step action is required to author completion requirements for this workflow.</p>
  <p *ngIf="ambiguous" class="configuration-warning" role="alert">Duplicate action or requirement identities need repair before this step can be edited.</p>
  <p *ngIf="!loading && legacyCompletingActions" class="configuration-warning">This step advances through an existing action. Replace it with a save-only action in Actions before adding requirements. Saved settings are preserved.</p>
  <p *ngIf="!loading && !availableProviders.length && !catalogError" class="configuration-warning">No supported requirement types are installed for this workflow.</p>
  <div *ngIf="error" class="configuration-warning" role="alert" tabindex="-1">{{ error }} <button uiButton [size]="undefined" [text]="true" type="button" *ngIf="catalogError" (click)="load()">Retry requirement types</button></div>
  <div class="requirements-layout">
    <ui-panel panelTitle="Requirements" [totalCount]="rows.length" [noGrow]="true" class="requirements-list-panel">
      <ul class="requirement-list" *ngIf="rows.length; else empty">
        <li *ngFor="let row of rows">
          <button uiButton [size]="undefined" [text]="selectedRequirementId !== row?.id || (!!draft && !originalId)" [outlined]="false" [wrap]="true" type="button" class="requirement-row"
            [attr.data-requirement-id]="row?.id" [attr.aria-current]="selectedRequirementId === row?.id && (!draft || originalId) ? 'true' : null"
            [class.requirement-row--selected]="selectedRequirementId === row?.id && (!draft || originalId)" (click)="select(row)">
            <i class="pi" [ngClass]="typeIcon(row?.provider || '')" aria-hidden="true"></i>
            <span><strong>{{ overviewLabel(row) }}</strong><span>{{ supported(row) ? typeLabel(row.provider) : 'Saved configuration unavailable' }}</span></span>
            <span *ngIf="selectedRequirementId === row?.id && (!draft || originalId)" class="requirement-row__selected" aria-hidden="true">✓</span>
          </button>
        </li>
      </ul>
      <ng-template #empty><p class="empty-note">No authored requirements. Add what staff must provide before completing this step.</p></ng-template>
      <details *ngIf="ambiguous"><summary>Saved configuration</summary><pre>{{ rows | json }}</pre></details>
    </ui-panel>
    <ng-template #requirementDetailHeader>
      <div class="editor-heading"><div>
        <span *ngIf="detailRequirement" class="section-eyebrow">{{ typeLabel(detailRequirement!.provider) }} requirement</span>
        <h4 class="requirement-detail-heading" tabindex="-1">{{ detailRequirement ? requirementTypeLabel(detailRequirement) : 'Requirement settings' }}</h4>
        <span *ngIf="draft && !originalId" class="new-requirement">New requirement · pending input</span>
      </div></div>
    </ng-template>
    <ui-panel [headerTemplate]="requirementDetailHeader" [noGrow]="true" class="requirement-settings-panel">
      <div *ngIf="!draft && selectedRow as row" class="requirement-inspection">
        <p>{{ supported(row) ? detail(row) : overviewDescription(row) }}</p>
        <p *ngIf="!canAuthor">This saved requirement is read-only.</p>
        <button uiButton [size]="undefined" [text]="true" type="button" *ngIf="row.provider === providers.form && supported(row)" [disabled]="interactionBlocked" (click)="openForm(row)">View form</button>
        <details><summary>Technical details</summary><pre>{{ row | json }}</pre></details>
        <button uiButton [size]="undefined" severity="danger" [outlined]="true" type="button" class="remove-requirement" [disabled]="disabled || ambiguous" (click)="remove(row)">Remove requirement</button>
        <p>Removes only this binding. Keeps its form, actions, Staff page content and recorded history.</p>
      </div>
      <p *ngIf="!draft && !selectedRow" class="empty-note">Select a requirement to inspect its settings, or add a requirement.</p>
    <div *ngIf="draft as requirement" class="requirement-form">
      <p *ngIf="requirement.provider === providers.form" class="requirement-fixed-type"><strong>Requirement type</strong> Form</p>
      <ng-container *ngIf="requirement.provider === providers.sdmf">
        <p>
          Uses the SDMF analysis block on this step’s Staff page and inherits
          its hearing source. All 13 rated factors and a summary must be
          completed, then certified or explicitly confirmed for the current
          visit.
        </p>
        <p>
          Configure the hearing source on the SDMF block. Removing this
          requirement keeps the block and recorded analysis.
        </p>
      </ng-container>
      <ng-container
        *ngIf="
          requirement.provider === providers.schedulingRequested ||
          requirement.provider === providers.hearingScheduled
        "
      >
        <p>
          Uses the hearing linked to this workflow. Request scheduling must run
          on every path before this step.
        </p>
        <p *ngIf="requirement.provider === providers.hearingScheduled">
          Staff must book the hearing within its current scheduling dates before
          completing this step.
        </p>
        <p *ngIf="requirement.provider === providers.schedulingRequested">
          An active scheduling request is required. Booking a date is a separate
          requirement.
        </p>
      </ng-container>
      <ng-container *ngIf="requirement.provider === providers.notices">
        <p>
          Applying adds the notice workspace and its actions together with
          Complete step. Staff manage recipients, prepare notices and record
          external sending directly on the page. Responses are optional unless
          required below. Removing this requirement preserves recorded notice
          history.
        </p>
        <section class="notice-source" aria-label="Hearing source">
          @let source = noticeSourceResolution;
          @if (source.kind === "blocked") {
            <p class="configuration-warning" role="status">
              {{ source.message }}
            </p>
          } @else if (noticeSourceChoices.length > 1) {
            <label for="requirement-notice-source">Hearing source</label>
            <select
              uiControl
              id="requirement-notice-source"
              [disabled]="disabled"
              [(ngModel)]="noticeSourceField"
              (ngModelChange)="changed()"
            >
              <option value="">Choose a Hearing reference</option>
              @for (field of noticeSourceChoices; track field.key) {
                <option [value]="field.key">{{ field.label }}</option>
              }
            </select>
            <p>
              This workflow has more than one Hearing reference. Choose the
              hearing this step's notices concern.
            </p>
          } @else if (source.kind === "resolved") {
            <p class="notice-source-summary" tabindex="-1">
              @if (pendingNoticeReference) {
                <strong>Hearing: supplied by workflow launch</strong>
              } @else {
                <strong>Hearing source: {{ source.label }}</strong>
              }
            </p>
            @if (pendingNoticeReference) {
              <p>
                Uses the hearing this workflow is launched from automatically.
                Starting from the workflow list asks staff to select the actual
                hearing instead.
              </p>
              <p>Apply connects this step's notice work to that hearing.</p>
            } @else if (source.origin === "step") {
              <p>Inherited from this step's shared Hearing source.</p>
            } @else if (source.origin === "workflow") {
              <p>Uses the hearing linked to this workflow automatically.</p>
            } @else {
              <p>Uses the only available Hearing reference automatically.</p>
            }
          } @else if (source.kind === "choice") {
            <p class="configuration-warning" role="status">
              {{ source.issue }} Cancel and reopen this requirement to use the
              current Hearing reference.
            </p>
          } @else {
            <p class="configuration-warning" role="alert">
              {{
                noticeReferenceError ||
                  "The workflow needs a hearing connection. Cancel and reopen this requirement to configure it."
              }}
            </p>
          }
        </section>
        <phms-notice-policy-editor
          [value]="requirement.settings['policyJson']"
          [disabled]="disabled"
          (valueChange)="setNoticePolicy($event)"
        />
      </ng-container>
      <ng-container *ngIf="requirement.provider === providers.form">
        <label for="requirement-form-source">Form to complete</label>
        <select
          uiControl
          id="requirement-form-source"
          required
          [disabled]="disabled"
          [ngModel]="requirement.settings['formId'] || ''"
          (ngModelChange)="chooseForm($event)"
        >
          <option value="">Choose a configured form</option>
          <option
            *ngFor="let command of formSources"
            [value]="command.captureForm?.id"
          >
            {{ command.captureForm?.title || commandLabel(command) }}
          </option>
        </select>
        <button
          uiButton
          severity="primary"
          [outlined]="false"
          *ngIf="canAddForm && !originalId"
          type="button"
          [disabled]="disabled"
          (click)="addSource('form')"
        >
          Add form
        </button>
        <button uiButton [size]="undefined" [text]="true" type="button" *ngIf="canOpenForm" [disabled]="interactionBlocked" (click)="openForm(requirement)">{{ canAuthor ? 'Edit form' : 'View form' }}</button>
        <p>Forms that keep the step open are eligible. Required fields and their constraints are edited in the form.</p>
      </ng-container>
      <ng-container *ngIf="requirement.provider === providers.document">
        <label for="requirement-document-name">Document name</label>
        <input
          uiControl
          id="requirement-document-name"
          type="text"
          maxlength="120"
          placeholder="e.g. Proof of address"
          required
          [disabled]="disabled"
          [(ngModel)]="requirement.settings['documentTypeLabel']"
          (ngModelChange)="changed()"
        />
        <fieldset>
          <legend>Accepted file formats</legend>
          <label *ngFor="let format of formats" class="check-label"
            ><input
              uiControl
              type="checkbox"
              [disabled]="disabled"
              [checked]="checked('allowedFormats', format.value)"
              (change)="
                toggle(
                  'allowedFormats',
                  format.value,
                  $any($event.target).checked
                )
              "
            />{{ format.label }}</label
          >
        </fieldset>
      </ng-container>
      <ng-container *ngIf="requirement.provider === providers.decision">
        <label for="requirement-decision-source"
          >Action that records the decision</label
        >
        <select
          uiControl
          id="requirement-decision-source"
          [disabled]="disabled"
          [ngModel]="decisionIdentity()"
          (ngModelChange)="chooseDecision($event)"
        >
          <option value="">Choose a recorded decision action</option>
          <option
            *ngFor="let command of decisionSources"
            [value]="commandId(command)"
          >
            {{ commandLabel(command) }}
          </option>
        </select>
        <button
          uiButton
          severity="primary"
          [outlined]="false"
          *ngIf="canAddDecision && !originalId"
          type="button"
          [disabled]="disabled"
          (click)="addSource('decision')"
        >
          Add Record review action
        </button>
        <p *ngIf="sourceDecision">
          Recorded results:
          <span *ngFor="let outcome of outcomeChoices; let last = last"
            >{{ outcome.replaceAll("_", " ") }}{{ last ? "." : " or " }}</span
          >
          A recorded result meets this requirement.
        </p>
        <p *ngIf="requiresReviewRequest">
          Request a review first; another person records the result. Both
          actions keep the step open.
        </p>
      </ng-container>
      <phms-workflow-voting-configuration
        *ngIf="isVoting(requirement)"
        [document]="document!"
        [nodeId]="nodeId!"
        [code]="votingCode"
        [provider]="requirement.provider"
        [editorKey]="requirement.id"
        [initialRequirement]="requirement.settings"
        [capabilities]="capabilities"
        [disabled]="disabled"
        [deferred]="true"
        (changed)="changed()"
      />
      <ng-container
        *ngIf="
          requirement.provider === providers.quorum && requirement.version === 1
        "
      >
        <label for="requirement-quorum-source">Configured voting source</label>
        <select
          uiControl
          id="requirement-quorum-source"
          [disabled]="disabled"
          [(ngModel)]="requirement.settings['sourceConfiguration']"
          (ngModelChange)="changed()"
        >
          <option value="">Choose a configured source</option>
          <option
            *ngFor="let choice of quorumChoices('sourceConfiguration')"
            [value]="choice.value"
          >
            {{ choice.label }}
          </option>
        </select>
        <label for="requirement-quorum-threshold">Required participants</label>
        <input
          uiControl
          id="requirement-quorum-threshold"
          type="number"
          min="1"
          max="256"
          step="1"
          [disabled]="disabled"
          [ngModel]="requirement.settings['quorumThreshold'] || ''"
          (ngModelChange)="setQuorumThreshold($event)"
        />
        <label for="requirement-quorum-approval">Approval rule</label>
        <select
          uiControl
          id="requirement-quorum-approval"
          [disabled]="disabled"
          [(ngModel)]="requirement.settings['approvalRuleCode']"
          (ngModelChange)="changed()"
        >
          <option value="">Choose approval rule</option>
          <option
            *ngFor="let choice of quorumChoices('approvalRuleCode')"
            [value]="choice.value"
          >
            {{ choice.label }}
          </option>
        </select>
        <label for="requirement-quorum-denominator">Approval denominator</label>
        <select
          uiControl
          id="requirement-quorum-denominator"
          [disabled]="disabled"
          [(ngModel)]="requirement.settings['denominatorCode']"
          (ngModelChange)="changed()"
        >
          <option value="">Choose denominator</option>
          <option
            *ngFor="let choice of quorumChoices('denominatorCode')"
            [value]="choice.value"
          >
            {{ choice.label }}
          </option>
        </select>
        <label for="requirement-quorum-abstention"
          >Abstentions toward quorum</label
        >
        <select
          uiControl
          id="requirement-quorum-abstention"
          [disabled]="disabled"
          [(ngModel)]="requirement.settings['abstentionQuorumCode']"
          (ngModelChange)="changed()"
        >
          <option value="">Choose abstention rule</option>
          <option
            *ngFor="let choice of quorumChoices('abstentionQuorumCode')"
            [value]="choice.value"
          >
            {{ choice.label }}
          </option>
        </select>
        <label for="requirement-quorum-tie"
          >Tied approval and rejection votes</label
        >
        <select
          uiControl
          id="requirement-quorum-tie"
          [disabled]="disabled"
          [(ngModel)]="requirement.settings['tieCode']"
          (ngModelChange)="changed()"
        >
          <option value="">Choose tie rule</option>
          <option
            *ngFor="let choice of quorumChoices('tieCode')"
            [value]="choice.value"
          >
            {{ choice.label }}
          </option>
        </select>
        <fieldset>
          <legend>Calculation results permitting completion</legend>
          <label
            *ngFor="let choice of quorumChoices('outcomeCodes')"
            class="check-label"
          >
            <input
              uiControl
              type="checkbox"
              [disabled]="disabled"
              [checked]="checked('outcomeCodes', choice.value)"
              (change)="
                toggle(
                  'outcomeCodes',
                  choice.value,
                  $any($event.target).checked
                )
              "
            />{{ choice.label }}
          </label>
        </fieldset>
        <p>
          Staff evaluate the owning module's current votes, then complete this
          step if a selected result is recorded. Votes cast includes approval
          and rejection only. This requirement does not sign or finalize a
          hearing outcome.
        </p>
      </ng-container>
      <section class="requirement-check" aria-label="What this checks">
        <h5>What this checks</h5>
        <p *ngIf="requirement.provider === providers.form">All visible required answers must be saved for this visit.</p>
        <p *ngIf="requirement.provider === providers.document">A new upload is required for this visit. Earlier case documents do not qualify.</p>
        <p>This check is defined by the requirement type. Actions can enforce additional module-owned checks.</p>
      </section>
      <details class="requirement-technical"><summary>Technical details</summary><pre>{{ requirement | json }}</pre></details>
      <div class="requirement-detail-actions">
        <button uiButton [size]="undefined" severity="danger" [outlined]="true" type="button" [disabled]="originalId ? disabled || ambiguous : interactionBlocked" (click)="originalId && selectedRow ? remove(selectedRow!) : cancelPendingChanges()">{{ originalId ? 'Remove requirement' : 'Discard new requirement' }}</button>
        <button uiButton [size]="undefined" [text]="true" type="button" *ngIf="originalId && hasPendingChanges()" (click)="cancelPendingChanges()">Discard unapplied edit</button>
        <p>{{ originalId ? 'Removes only this binding. Keeps its form, actions, Staff page content and recorded history.' : 'Discards only this pending binding. Keeps other draft changes and any companion configuration already applied.' }}</p>
      </div>
    </div>
    </ui-panel>
  </div>
  <details class="completion-help"><summary>How completion works</summary><p>Every requirement must be met during the current visit. Staff save each item, then choose Complete step. Saving keeps the step open. Actions may enforce additional checks.</p></details>
</section>
